import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { loadGoogleMaps } from "@/lib/maps";
import { closeSuggestions, keepFieldVisible } from "@/lib/mobileView";

export function AddressStep({ cfg, value, service, onConfirm, onFail, onManual, onPlaceSelect }: any) {
  const [svc, setSvc] = useState<any>(service || (cfg.services.length === 1 ? cfg.services[0] : null));
  const host = useRef<HTMLDivElement>(null);
  const onSelectRef = useRef(onPlaceSelect);
  onSelectRef.current = onPlaceSelect;
  const [picked, setPicked] = useState<any>(value || null);
  const [text, setText] = useState(value?.formatted || "");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    let elRef: any = null;
    let unkeep: (() => void) | null = null;
    const authFail = () => onFail("maps_unavailable");
    window.addEventListener("iqw-maps-auth-failure", authFail);
    (async () => {
      try {
        const g = await loadGoogleMaps(cfg.maps.browser_key);
        const { PlaceAutocompleteElement } = await g.maps.importLibrary("places");
        if (cancelled || !host.current) return;
        const el = new PlaceAutocompleteElement({ includedRegionCodes: ["us"] });
        el.setAttribute("data-testid", "address-autocomplete");
        el.setAttribute("placeholder", "Start typing the property address");
        el.addEventListener("gmp-select", async (ev: any) => {
          try {
            const place = ev.placePrediction.toPlace();
            await place.fetchFields({ fields: ["formattedAddress", "location", "id", "addressComponents", "types"] });
            const pc = (place.addressComponents || []).find((c: any) => c.types.includes("postal_code"));
            const p = { formatted: place.formattedAddress, place_id: place.id, lat: place.location.lat(), lng: place.location.lng(), postal_code: pc?.shortText || pc?.longText || null,
              precise: (place.types || []).some((t: string) => ["street_address", "premise", "subpremise"].includes(t)) };
            setPicked(p); setText(p.formatted); setErr("");
            closeSuggestions(el);
            onSelectRef.current?.();
          } catch (_) { setErr("We couldn’t load that address. Try another, or request a manual quote."); }
        });
        el.addEventListener("gmp-error", () => { if (!cancelled) onFail("maps_unavailable"); });
        host.current.innerHTML = "";
        host.current.appendChild(el);
        elRef = el;
        unkeep = keepFieldVisible(el);
        setLoading(false);
      } catch (_) {
        if (!cancelled) onFail("maps_unavailable");
      }
    })();
    return () => { cancelled = true; window.removeEventListener("iqw-maps-auth-failure", authFail); unkeep?.(); elRef?.remove(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="address-step">
      <div>
        <label className="text-sm font-medium block mb-2">Property address</label>
        <div ref={host} className="min-h-[48px]" />
        {loading && <div className="h-12 rounded-lg bg-slate-100 animate-pulse" />}
      </div>
      {err && <p className="text-sm text-red-700" role="alert">{err}</p>}
      {picked && (
        <div className="rounded-xl border bg-slate-50 p-4 space-y-2 fade-up" data-testid="address-confirm-card">
          <p className="text-sm font-medium flex items-center gap-1.5"><MapPin className="h-4 w-4 brand-text" />Confirm the service address</p>
          <input className="w-full h-11 rounded-lg border px-3 text-[16px] bg-white brand-ring" value={text} onChange={(e) => setText(e.target.value)} data-testid="address-confirm-input" />
          {picked.precise === false && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="address-imprecise-warning">This doesn’t look like a specific house address. Pick your street address from the list, or request a manual quote below.</p>}
          <p className="text-[11px] text-slate-500">Edit if needed (e.g. add a unit number). This is the address the business will use.</p>
        </div>
      )}
      <div className="space-y-2" data-testid="service-step">
        <p className="text-sm font-medium">Service</p>
        {cfg.services.length === 0 && <p className="text-sm text-slate-600" data-testid="no-services">No services are published yet.</p>}
        {cfg.services.map((s: any) => (
          <button key={s.id} onClick={() => setSvc(s)} aria-pressed={svc?.id === s.id} data-testid={`service-option-${s.id.slice(0, 8)}`}
            className={`w-full min-h-[56px] rounded-xl border px-4 py-3 flex items-center gap-3 text-left transition-colors brand-ring ${svc?.id === s.id ? "border-[var(--brand)] bg-[color-mix(in_srgb,var(--brand)_8%,white)]" : "bg-white"}`}>
            <span className={`h-5 w-5 rounded-full border-2 shrink-0 ${svc?.id === s.id ? "border-[var(--brand)] bg-[var(--brand)]" : "border-slate-300"}`} />
            <span className="flex-1"><span className="font-medium block">{s.name}</span>
              <span className="text-xs text-slate-500">{s.pricing_unit === "linear_ft" ? "Measured in linear feet" : "Measured in square feet"}{s.note ? ` · ${s.note}` : ""}</span></span>
          </button>
        ))}
      </div>
      <button className="min-h-[44px] text-sm underline text-slate-600" onClick={onManual} data-testid="address-manual-link">Prefer not to measure, or can’t find your address? Request a manual quote</button>
      <div className="flex gap-2 pb-safe">
        <button className="h-12 flex-1 rounded-lg brand-btn font-semibold" disabled={!picked || !svc || text.trim().length < 3} onClick={() => { const { precise, ...a } = picked; onConfirm({ ...a, formatted: text.trim() }, svc); }} data-testid="address-confirm-button">
          Continue
        </button>
      </div>
    </div>
  );
}
